// src/receptionist/screens/Services.tsx
//
// The knowledge base. This screen IS what the AI is allowed to say: it quotes
// these prices and policies and nothing else (PRD §3.1), which is why the
// hint under the title says so in plain words. A service switched off is
// described but never offered for booking.

import { useEffect, useRef, useState } from "react";
import { BadgeCheckIcon, ChevronRightIcon, FileTextIcon, GripVerticalIcon, ImagePlusIcon, InfoIcon, ListChecksIcon, Loader2Icon, ScrollTextIcon, TrashIcon } from "lucide-react";
import { PageHeader, Card, CardHeader, CardBody, Button } from "../components/kit";
import { AddButton, TagPill } from "../components/ui";
import { forgetSetupState } from "../components/SetupBanner";
import {
    deleteServiceImage, getWhatsappState, signOffKnowledgeBase, uploadServiceImage,
    SERVICE_IMAGE_MAX_BYTES, SERVICE_IMAGE_TYPES, type WhatsappState,
} from "@service/receptionist";
import { useSnackbar } from "@hook/use-snackbar";
import { Input } from "@component/input";
import { Textarea } from "@component/textarea";
import { Label } from "@component/label";
import { Switch } from "@component/switch";
import { cn } from "@oura/lib/cn";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@component/dialog";
import type { MerchantConfig, Service } from "../merchant";
import type { MerchantScreenProps } from "../merchantState";

/** Minutes: a whole number, never negative (#4796, a -5 slipped in). 0 while typing; the save refuses it. */
function minutesFrom(raw: string): number {
    const n = Math.round(Number(raw));
    return Number.isFinite(n) && n > 0 ? n : 0;
}
/** No minus sign, exponent or plus in a duration or a price. */
function noSign(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "-" || e.key === "+" || e.key === "e" || e.key === "E") e.preventDefault();
}
/** A price: never negative. */
function priceFrom(raw: string): number {
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
}
/** The same rules on what the server sent and on what is saved, not only on what is
 *  typed: a negative already stored shows as 0, and a 0 duration is refused on save. */
function normalizeService(s: Service): Service {
    return {
        ...s,
        durationMinutes: minutesFrom(String(s.durationMinutes)),
        price: priceFrom(String(s.price)),
        firstVisitPrice: priceFrom(String(s.firstVisitPrice)),
    };
}
function normalizeConfig(m: MerchantConfig): MerchantConfig {
    return { ...m, services: m.services.map(normalizeService) };
}

/**
 * A service's picture (ourteam-backend#6681): the AI sends it when it asks which
 * treatment. Uploaded straight away, not on Save, so it needs a service the
 * server already has; a new row says to save first.
 *
 * A 64px tile, large enough to tell one treatment's photo from another's
 * (29 Oct 2026: at 40px an uploaded image was hard to see). Pressed, an image
 * opens at full size with Replace and Remove; an empty tile opens the file
 * picker.
 */
function ServiceImageCell({
    merchantId, service, saved, onImage,
}: {
    merchantId: string;
    service: Service;
    saved: boolean;
    onImage: (image: Pick<Service, "imageKey" | "imageUrl">) => void;
}) {
    const input = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);
    const [viewing, setViewing] = useState(false);
    const snackbar = useSnackbar();
    const name = service.name || "this service";
    const has = Boolean(service.imageUrl);

    const pick = async (file: File | undefined) => {
        if (!file) return;
        if (!SERVICE_IMAGE_TYPES.includes(file.type)) { snackbar.showError("Choose a JPEG, PNG or WebP image."); return; }
        if (file.size > SERVICE_IMAGE_MAX_BYTES) { snackbar.showError("The image must be 5 MB or smaller."); return; }
        setBusy(true);
        const result = await uploadServiceImage(merchantId, service.id, file);
        setBusy(false);
        if (!result.ok) { snackbar.showError(result.message); return; }
        const fresh = result.merchant.services.find((x) => x.id === service.id);
        onImage({ imageKey: fresh?.imageKey ?? null, imageUrl: fresh?.imageUrl ?? null });
    };
    const remove = async () => {
        setBusy(true);
        const result = await deleteServiceImage(merchantId, service.id);
        setBusy(false);
        if (!result.ok) { snackbar.showError(result.message); return; }
        onImage({ imageKey: null, imageUrl: null });
        setViewing(false);
    };

    return (
        <>
            <button
                type="button"
                disabled={!saved || busy}
                onClick={() => (has ? setViewing(true) : input.current?.click())}
                aria-label={has ? `View the image of ${name}` : `Add an image of ${name}`}
                title={!saved ? "Save the service first, then add its image" : has ? "View, replace or remove the image" : "Add an image (JPEG, PNG or WebP, up to 5 MB)"}
                className={cn(
                    "group relative grid h-16 w-16 flex-shrink-0 place-items-center overflow-hidden rounded-xl text-gray-400 transition-colors",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    has
                        ? "ring-1 ring-inset ring-black/[0.08]"
                        : "border border-dashed border-gray-300 bg-gray-50 hover:border-[#0A5FD1] hover:text-[#0A5FD1] disabled:hover:border-gray-300 disabled:hover:text-gray-400",
                )}
            >
                {busy ? <Loader2Icon className="h-5 w-5 animate-spin" />
                    : has ? (
                        <>
                            <img src={service.imageUrl ?? ""} alt="" className="h-full w-full object-cover" />
                            <span aria-hidden className="absolute inset-0 grid place-items-center bg-black/40 text-[11px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                                View
                            </span>
                        </>
                    ) : (
                        <span className="flex flex-col items-center gap-0.5">
                            <ImagePlusIcon className="h-5 w-5" />
                            <span className="text-[10px] font-medium">Add</span>
                        </span>
                    )}
            </button>
            <input
                ref={input}
                type="file"
                accept={SERVICE_IMAGE_TYPES.join(",")}
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; void pick(f); }}
            />
            <Dialog open={viewing} onOpenChange={(o) => { if (!busy) setViewing(o); }}>
                {/* Focus opens on Done, never on Remove: Enter must not delete the picture. */}
                <DialogContent className="grid-cols-[minmax(0,1fr)] sm:max-w-xl"
                    onOpenAutoFocus={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement).querySelector<HTMLButtonElement>("[data-autofocus]")?.focus(); }}>
                    <DialogHeader>
                        <DialogTitle>{name}</DialogTitle>
                        <DialogDescription>Sent with the other services&apos; pictures when the AI asks which treatment.</DialogDescription>
                    </DialogHeader>
                    <div className="grid place-items-center overflow-hidden rounded-xl bg-gray-50 ring-1 ring-inset ring-black/[0.06]">
                        {service.imageUrl && <img src={service.imageUrl} alt={name} className="max-h-[60vh] w-full object-contain" />}
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                        <Button variant="outline" disabled={busy} onClick={() => void remove()} className="text-red-600 hover:text-red-700">
                            <TrashIcon className="h-4 w-4" /> Remove image
                        </Button>
                        <Button variant="outline" disabled={busy} onClick={() => input.current?.click()}>
                            {busy ? <Loader2Icon className="h-4 w-4 animate-spin" /> : <ImagePlusIcon className="h-4 w-4" />} Replace image
                        </Button>
                        <Button data-autofocus disabled={busy} onClick={() => setViewing(false)}>Done</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

/** A small labelled number box with a unit before or after it ("RM", "min"). Never negative (see noSign). */
function NumberField({
    label, prefix, suffix, value, min, step, onValue, ...rest
}: {
    label: string;
    prefix?: string;
    suffix?: string;
    value: number;
    min?: number;
    step?: number;
    onValue: (raw: string) => void;
    "aria-label": string;
}) {
    return (
        <label className="block min-w-0">
            <span className="mb-0.5 block text-[11px] font-medium text-gray-500">{label}</span>
            <span className="flex h-9 items-center rounded-md border border-input bg-white px-2.5 text-sm focus-within:ring-2 focus-within:ring-[#0A5FD1]/30">
                {prefix && <span className="mr-1 text-xs text-gray-400">{prefix}</span>}
                <input
                    type="number"
                    value={value}
                    min={min}
                    step={step}
                    onKeyDown={noSign}
                    onChange={(e) => onValue(e.target.value)}
                    aria-label={rest["aria-label"]}
                    className="w-full min-w-0 bg-transparent text-gray-900 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                {suffix && <span className="ml-1 text-xs text-gray-400">{suffix}</span>}
            </span>
        </label>
    );
}

/** Where a dragged item would land on this one: before or after it, and whether the list runs across
 *  (a grid row, so the mark is a line on its left or right) or down (a line above or below it). */
type DropMark = { mark: "before" | "after"; across: boolean } | null;

/**
 * Reordering a list by dragging a handle, or with the arrow keys on it (29 Oct 2026: FAQ notes, then
 * services). The handle is the drag source and the item only a drop target, so text in an item's fields
 * never starts a drag (Jack, #4836). `handleAttr` names the handle's index attribute, so focus can follow
 * an item moved with the keyboard. The browser's own drag and drop: no dependency.
 */
function useReorder(count: number, move: (from: number, to: number) => void, handleAttr: string) {
    const [drag, setDrag] = useState<number | null>(null);
    const [drop, setDrop] = useState<{ index: number } & NonNullable<DropMark> | null>(null);
    const end = () => { setDrag(null); setDrop(null); };
    const markAt = (e: React.DragEvent<HTMLElement>): NonNullable<DropMark> => {
        const box = e.currentTarget.getBoundingClientRect();
        const list = e.currentTarget.parentElement?.getBoundingClientRect();
        // Narrower than most of its list: one of several across a grid row.
        const across = !!list && box.width < list.width * 0.75;
        const before = across ? e.clientX < box.left + box.width / 2 : e.clientY < box.top + box.height / 2;
        return { mark: before ? "before" : "after", across };
    };
    return {
        dragging: (i: number) => drag === i,
        mark: (i: number): DropMark => (drop && drag !== null && drag !== i && drop.index === i ? { mark: drop.mark, across: drop.across } : null),
        /** Keyboard: one place up or down; focus follows the item (the lists are keyed by index). */
        moveBy: (i: number, by: -1 | 1) => {
            const to = i + by;
            if (to < 0 || to >= count) return;
            move(i, to);
            window.requestAnimationFrame(() => document.querySelector<HTMLElement>(`[${handleAttr}="${to}"]`)?.focus());
        },
        onDragStart: (i: number) => (e: React.DragEvent<HTMLButtonElement>) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", String(i));
            // Show the whole item moving, not just the small handle.
            const item = e.currentTarget.closest("li");
            if (item) e.dataTransfer.setDragImage(item, 24, 20);
            setDrag(i);
        },
        onDragOver: (i: number) => (e: React.DragEvent<HTMLLIElement>) => {
            if (drag === null) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            const at = markAt(e);
            if (drop?.index !== i || drop.mark !== at.mark || drop.across !== at.across) setDrop({ index: i, ...at });
        },
        onDrop: (i: number) => (e: React.DragEvent<HTMLLIElement>) => {
            e.preventDefault();
            if (drag === null) { end(); return; }
            // The landing index once the dragged item has left its place.
            let to = markAt(e).mark === "before" ? i : i + 1;
            if (drag < to) to -= 1;
            if (to !== drag) move(drag, to);
            end();
        },
        onDragEnd: end,
    };
}

/** The line where a dragged item would land. */
function DropLine({ mark }: { mark: DropMark }) {
    if (!mark) return null;
    return (
        <span
            aria-hidden
            className={cn(
                "pointer-events-none absolute z-10 rounded-full bg-[#0A5FD1]",
                mark.across
                    ? cn("inset-y-2 w-0.5", mark.mark === "before" ? "-left-[7px]" : "-right-[7px]")
                    : cn("inset-x-2 h-0.5", mark.mark === "before" ? "-top-px" : "-bottom-px"),
            )}
        />
    );
}

/** One FAQ note: its title and a one-line preview; pressed, the title and the answer to edit.
 *  Dragged by its handle to reorder (29 Oct 2026), or moved with the arrow keys on the handle;
 *  the order is the order the AI reads them in, saved with Save changes. */
function FaqNoteRow({
    index, count, label, body, open, onToggle, onChange, onRemove, onMove,
    dragging, mark, onDragStart, onDragOver, onDrop, onDragEnd,
}: {
    index: number;
    count: number;
    label: string;
    body: string;
    open: boolean;
    onToggle: () => void;
    onChange: (next: { label?: string; body?: string }) => void;
    onRemove: () => void;
    /** Move this note by one place: -1 up, +1 down. */
    onMove: (by: -1 | 1) => void;
    dragging: boolean;
    mark: DropMark;
    /** The handle is the drag source, never the row: text in an open note stays selectable (Jack, #4836). */
    onDragStart: (e: React.DragEvent<HTMLButtonElement>) => void;
    onDragOver: (e: React.DragEvent<HTMLLIElement>) => void;
    onDrop: (e: React.DragEvent<HTMLLIElement>) => void;
    onDragEnd: () => void;
}) {
    const title = label.trim() || "Untitled note";
    return (
        <li
            onDragOver={onDragOver}
            onDrop={onDrop}
            className={cn(
                "relative",
                open ? "bg-gray-50/60" : "bg-white",
                dragging && "opacity-40",
            )}
        >
            <DropLine mark={mark} />
            <div className="flex items-center gap-1 pr-2">
                <button
                    type="button"
                    data-faq-handle={index}
                    aria-label={`Move ${title}. Use the up and down arrow keys, or drag.`}
                    title="Drag to reorder"
                    draggable
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowUp" && index > 0) { e.preventDefault(); onMove(-1); }
                        if (e.key === "ArrowDown" && index < count - 1) { e.preventDefault(); onMove(1); }
                    }}
                    className="ml-1 grid h-8 w-6 flex-shrink-0 cursor-grab place-items-center rounded text-gray-300 hover:text-gray-500 active:cursor-grabbing focus-visible:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A5FD1]/40"
                >
                    <GripVerticalIcon className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={onToggle}
                    aria-expanded={open}
                    className="flex min-w-0 flex-1 items-center gap-2.5 py-3 pl-1 pr-3.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0A5FD1]/40"
                >
                    <ChevronRightIcon aria-hidden className={cn("h-4 w-4 flex-shrink-0 text-gray-400 transition-transform", open && "rotate-90")} />
                    <span className="min-w-0 flex-1">
                        <span className={cn("block truncate text-sm font-medium", label.trim() ? "text-gray-900" : "italic text-gray-400")}>{title}</span>
                        {!open && (
                            <span className={cn("block truncate text-xs", body.trim() ? "text-gray-500" : "italic text-gray-400")}>
                                {body.trim() || "No answer yet"}
                            </span>
                        )}
                    </span>
                </button>
                <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${label.trim() || "this note"}`}
                    title="Remove this note"
                    className="h-8 w-8 flex-shrink-0 text-gray-400 hover:text-red-600"
                    onClick={onRemove}
                >
                    <TrashIcon className="h-4 w-4" />
                </Button>
            </div>
            {open && (
                <div className="space-y-3 px-3.5 pb-3.5 pl-[3.75rem]">
                    <div className="space-y-1.5">
                        <Label htmlFor={`faq-label-${index}`} className="text-xs font-medium text-gray-600">What customers ask about</Label>
                        <Input
                            id={`faq-label-${index}`}
                            autoFocus={!label}
                            value={label}
                            placeholder="e.g. Parking and directions"
                            onChange={(e) => onChange({ label: e.target.value })}
                            className="h-9 bg-white font-medium"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor={`faq-${index}`} className="text-xs font-medium text-gray-600">The answer</Label>
                        <Textarea
                            id={`faq-${index}`}
                            value={body}
                            placeholder="The answer, in your own words."
                            onChange={(e) => onChange({ body: e.target.value })}
                            className="min-h-[96px] resize-y bg-white"
                        />
                    </div>
                </div>
            )}
        </li>
    );
}

/** The product's switch. */
function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
    return <Switch checked={on} onCheckedChange={onChange} aria-label={label} className="flex-shrink-0" />;
}

function when(iso: string): string {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/**
 * "Knowledge base signed off", the Setup checklist's last item (#4791,
 * ourteam-backend#6674). Its own card at the top of Services, because this
 * page IS the knowledge base. A later edit does not undo it (product owner,
 * 28 Oct 2026). Nothing shows until the state is read, or if the read fails.
 */
export function KnowledgeSignOff({ merchantId, dirty }: { merchantId: string; dirty: boolean }) {
    const snackbar = useSnackbar();
    const [state, setState] = useState<WhatsappState | null>(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        let live = true;
        getWhatsappState(merchantId).then((s) => { if (live) setState(s); }).catch(() => undefined);
        return () => { live = false; };
    }, [merchantId]);
    if (!state) return null;
    const signed = state.checklist.kbSignedOff;
    const signOff = async () => {
        setBusy(true);
        try {
            setState(await signOffKnowledgeBase(merchantId));
            forgetSetupState(merchantId);
        } catch {
            snackbar.showError("Couldn't sign off the knowledge base. Try again.");
        } finally {
            setBusy(false);
        }
    };
    return (
        <Card className="mb-5 pb-2">
            <CardHeader
                title="Knowledge base sign-off"
                icon={BadgeCheckIcon}
                description={signed
                    ? [state.kbSignedOffAt ? `Signed off ${when(state.kbSignedOffAt)}` : "Signed off", state.kbSignedOffBy ? `by ${state.kbSignedOffBy}` : ""].filter(Boolean).join(" ") + "."
                    : "Confirm these services, prices, policies and FAQ notes are right. The AI quotes only what is here."}
                aside={signed
                    ? <TagPill tone="green">Signed off</TagPill>
                    : (
                        <span className="flex items-center gap-2">
                            {dirty && <span className="text-xs text-gray-500">Save your changes first.</span>}
                            <Button size="sm" disabled={dirty || busy} onClick={() => void signOff()}>
                                {busy ? "Signing off…" : "Sign off"}
                            </Button>
                        </span>
                    )}
            />
        </Card>
    );
}

export function ServicesScreen({ merchant: server, onSave }: MerchantScreenProps) {
    // A local draft of the server's copy. Whenever the prop changes — after a
    // save returned the server's object, or after a failed save re-synced —
    // the draft is replaced wholesale, so the form never shows an optimistic
    // copy the server did not accept.
    const [merchant, setMerchant] = useState<MerchantConfig>(() => normalizeConfig(server));
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    /** A service being added. A dialog rather than a blank row, so a half-typed
     *  treatment is never one stray Save away from being something the AI quotes. */
    const [serviceDraft, setServiceDraft] = useState<Omit<Service, "id"> | null>(null);
    /** The FAQ note open for editing; the rest read as one line each. */
    const [openNote, setOpenNote] = useState<number | null>(null);
    /** Moves an item of a list from one index to another. */
    const reordered = <T,>(items: T[], from: number, to: number): T[] => {
        const next = [...items];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
    };
    /** FAQ notes: the open note stays open wherever it lands. */
    const noteOrder = useReorder(merchant.faqNotes.length, (from, to) => {
        const notes = reordered(merchant.faqNotes, from, to);
        const opened = openNote === null ? null : merchant.faqNotes[openNote];
        patch({ faqNotes: notes });
        setOpenNote(opened ? notes.indexOf(opened) : null);
    }, "data-faq-handle");
    /** Services: the order the AI lists them in, and sends their pictures in. */
    const serviceOrder = useReorder(merchant.services.length, (from, to) => {
        patch({ services: reordered(merchant.services, from, to) });
    }, "data-service-handle");
    useEffect(() => { setMerchant(normalizeConfig(server)); setDirty(false); setServiceDraft(null); }, [server]);
    const save = async () => {
        setSaving(true);
        try { if (await onSave(normalizeConfig(merchant))) setDirty(false); } finally { setSaving(false); }
    };

    const patch = (next: Partial<MerchantConfig>) => {
        setMerchant((m) => ({ ...m, ...next }));
        setDirty(true);
    };
    const patchService = (id: string, next: Partial<Service>) =>
        patch({ services: merchant.services.map((s) => (s.id === id ? { ...s, ...next } : s)) });
    /** An image is already saved on the server, so it changes the row without making the form dirty. */
    const setImage = (id: string, image: Pick<Service, "imageKey" | "imageUrl">) =>
        setMerchant((m) => ({ ...m, services: m.services.map((s) => (s.id === id ? { ...s, ...image } : s)) }));
    const savedIds = new Set(server.services.map((s) => s.id));

    return (
        <div>
            <PageHeader
                title="Services"
                subtitle="The AI quotes only what is on this page. If something is not here, it says so and asks your team."
                status={`${merchant.services.length} ${merchant.services.length === 1 ? "service" : "services"}, ${merchant.services.filter((x) => x.bookable).length} bookable · ${merchant.faqNotes.length} FAQ ${merchant.faqNotes.length === 1 ? "note" : "notes"}`}
                action={<Button disabled={!dirty || saving} onClick={() => void save()}>{saving ? "Saving…" : "Save changes"}</Button>}
            />

            {/* Keyed by merchant, so no state or late response of one merchant's card reaches another's (Jack, #4792). */}
            <KnowledgeSignOff key={server.id} merchantId={server.id} dirty={dirty} />

            <Card className="mb-5">
                <CardHeader
                    title="Services and prices"
                    icon={ListChecksIcon}
                    description="Switch off anything the AI should describe but never book. A picture is sent when the AI asks which treatment. Drag the handle to change the order the AI lists them in."
                />
                <CardBody className="pt-0">
                    {/* A card per service, two across on a wide screen (29 Oct 2026): the table
                        spread six short inputs over the whole width and left most of it empty. */}
                    {merchant.services.length === 0 ? (
                        <p className="m-0 rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                            No services yet. The AI quotes and books only what is listed here, so the booking flow has
                            nothing to offer until you add one.
                        </p>
                    ) : (
                        <ul className="list-plain m-0 grid gap-3 p-0 lg:grid-cols-2">
                            {merchant.services.map((s, i) => (
                                <li
                                    key={s.id}
                                    onDragOver={serviceOrder.onDragOver(i)}
                                    onDrop={serviceOrder.onDrop(i)}
                                    className={cn(
                                        "relative flex gap-2 rounded-xl border p-3 pl-1.5 transition-colors",
                                        s.bookable ? "border-gray-200 bg-white" : "border-gray-200 bg-gray-50",
                                        serviceOrder.dragging(i) && "opacity-40",
                                    )}
                                >
                                    <DropLine mark={serviceOrder.mark(i)} />
                                    <button
                                        type="button"
                                        data-service-handle={i}
                                        aria-label={`Move ${s.name || "this service"}. Use the arrow keys, or drag.`}
                                        title="Drag to reorder"
                                        draggable
                                        onDragStart={serviceOrder.onDragStart(i)}
                                        onDragEnd={serviceOrder.onDragEnd}
                                        onKeyDown={(e) => {
                                            if (e.key === "ArrowUp" || e.key === "ArrowLeft") { e.preventDefault(); serviceOrder.moveBy(i, -1); }
                                            if (e.key === "ArrowDown" || e.key === "ArrowRight") { e.preventDefault(); serviceOrder.moveBy(i, 1); }
                                        }}
                                        className="grid w-5 flex-shrink-0 cursor-grab place-items-center self-stretch rounded text-gray-300 hover:text-gray-500 active:cursor-grabbing focus-visible:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A5FD1]/40"
                                    >
                                        <GripVerticalIcon className="h-4 w-4" />
                                    </button>
                                    <ServiceImageCell
                                        merchantId={server.id}
                                        service={s}
                                        saved={savedIds.has(s.id)}
                                        onImage={(image) => setImage(s.id, image)}
                                    />
                                    <div className="min-w-0 flex-1 space-y-2">
                                        <div className="flex items-center gap-2">
                                            <Input
                                                value={s.name}
                                                aria-label="Service name"
                                                onChange={(e) => patchService(s.id, { name: e.target.value })}
                                                className="h-9 min-w-0 flex-1 font-medium"
                                            />
                                            <label className="flex flex-shrink-0 items-center gap-1.5 text-xs text-gray-500">
                                                <Toggle
                                                    on={s.bookable}
                                                    label={`${s.name} bookable`}
                                                    onChange={(v) => patchService(s.id, { bookable: v })}
                                                />
                                                <span className="hidden sm:inline">Bookable</span>
                                            </label>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                aria-label={`Remove ${s.name || "this service"}`}
                                                title="Remove this service"
                                                className="h-8 w-8 flex-shrink-0 text-gray-400 hover:text-red-600"
                                                onClick={() => patch({ services: merchant.services.filter((x) => x.id !== s.id) })}
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </Button>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2 tabular-nums">
                                            <NumberField
                                                label="Minutes"
                                                suffix="min"
                                                value={s.durationMinutes}
                                                min={1}
                                                step={5}
                                                aria-label={`${s.name} duration in minutes`}
                                                onValue={(v) => patchService(s.id, { durationMinutes: minutesFrom(v) })}
                                            />
                                            <NumberField
                                                label="Price"
                                                prefix="RM"
                                                value={s.price}
                                                min={0}
                                                aria-label={`${s.name} price in RM`}
                                                onValue={(v) => patchService(s.id, { price: priceFrom(v) })}
                                            />
                                            <NumberField
                                                label="First visit"
                                                prefix="RM"
                                                value={s.firstVisitPrice}
                                                min={0}
                                                aria-label={`${s.name} first-visit price in RM`}
                                                onValue={(v) => patchService(s.id, { firstVisitPrice: priceFrom(v) })}
                                            />
                                        </div>
                                        {!s.bookable && (
                                            <p className="m-0 text-xs text-gray-500">Described when asked, never offered for booking.</p>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="mt-3">
                        <AddButton onClick={() => setServiceDraft({ name: "", durationMinutes: 60, price: 0, firstVisitPrice: 0, bookable: true })}>
                            Add service
                        </AddButton>
                    </div>

                    {/* A dialog, matching how a person is added on Staff. A blank
                        row in the table would be one stray Save away from the AI
                        quoting a nameless treatment at RM0. */}
                    <Dialog open={serviceDraft !== null} onOpenChange={(o) => { if (!o) setServiceDraft(null); }}>
                        <DialogContent className="sm:max-w-lg">
                            <DialogHeader>
                                <DialogTitle>Add a service</DialogTitle>
                                <DialogDescription>
                                    The AI quotes this price and offers this slot length. Switch off
                                    &ldquo;bookable&rdquo; for something it should describe but never book.
                                </DialogDescription>
                            </DialogHeader>
                            {serviceDraft !== null && (
                                <div className="space-y-3">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="rcp-service-name" className="text-sm font-medium">Service name</Label>
                                        <Input
                                            id="rcp-service-name"
                                            autoFocus
                                            value={serviceDraft.name}
                                            placeholder="e.g. Hydra Glow Facial"
                                            onChange={(e) => setServiceDraft({ ...serviceDraft, name: e.target.value })}
                                            className="bg-white"
                                        />
                                    </div>
                                    <div className="grid gap-3 sm:grid-cols-3">
                                        <div className="space-y-1.5">
                                            <Label htmlFor="rcp-service-minutes" className="text-sm font-medium">Minutes</Label>
                                            <Input
                                                id="rcp-service-minutes"
                                                type="number"
                                                value={serviceDraft.durationMinutes}
                                                min={1}
                                            onKeyDown={noSign}
                                                step={5}
                                                onChange={(e) => setServiceDraft({ ...serviceDraft, durationMinutes: minutesFrom(e.target.value) })}
                                                className="bg-white"
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label htmlFor="rcp-service-price" className="text-sm font-medium">Price (RM)</Label>
                                            <Input
                                                id="rcp-service-price"
                                                type="number"
                                                min={0}
                                            onKeyDown={noSign}
                                                value={serviceDraft.price}
                                                onChange={(e) => {
                                                    const price = priceFrom(e.target.value);
                                                    // First-visit price follows the standard one until it
                                                    // is set apart, so the common case needs one number.
                                                    setServiceDraft((d) => d && ({
                                                        ...d,
                                                        price,
                                                        firstVisitPrice: d.firstVisitPrice === d.price ? price : d.firstVisitPrice,
                                                    }));
                                                }}
                                                className="bg-white"
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label htmlFor="rcp-service-first" className="text-sm font-medium">First visit</Label>
                                            <Input
                                                id="rcp-service-first"
                                                type="number"
                                                value={serviceDraft.firstVisitPrice}
                                                min={0}
                                            onKeyDown={noSign}
                                                onChange={(e) => setServiceDraft({ ...serviceDraft, firstVisitPrice: priceFrom(e.target.value) })}
                                                className="bg-white"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3 pt-1">
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-gray-900">Bookable</p>
                                            <p className="text-xs text-gray-500">Off means the AI describes it but never offers a slot.</p>
                                        </div>
                                        <Switch
                                            checked={serviceDraft.bookable}
                                            onCheckedChange={(v) => setServiceDraft({ ...serviceDraft, bookable: v })}
                                            aria-label="Bookable"
                                            className="mt-0.5 flex-shrink-0"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500">Nothing is sent until you press Save changes.</p>
                                </div>
                            )}
                            <div className="flex justify-end gap-2">
                                <Button variant="outline" onClick={() => setServiceDraft(null)}>Cancel</Button>
                                <Button
                                    disabled={serviceDraft === null || !serviceDraft.name.trim()}
                                    onClick={() => {
                                        if (serviceDraft === null) return;
                                        patch({
                                            services: [...merchant.services, {
                                                ...serviceDraft,
                                                id: `s_${Math.random().toString(36).slice(2, 8)}`,
                                                name: serviceDraft.name.trim(),
                                            }],
                                        });
                                        setServiceDraft(null);
                                    }}
                                >
                                    Add service
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>

                    <p className="mt-3 flex items-start gap-1.5 text-xs text-gray-500">
                        <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        Suitability questions about a treatment always go to your team, whatever is set here.
                    </p>
                </CardBody>
            </Card>

            {/* Policies across the page, the two side by side; FAQ notes as a list under it
                (29 Oct 2026): side by side, short Policies left a column of empty space and the
                notes were a wall of boxes. */}
            <Card className="mb-5">
                <CardHeader title="Policies" icon={ScrollTextIcon} description="Quoted back before the AI cancels anything." />
                <CardBody className="grid gap-4 pt-0 md:grid-cols-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="cancellationPolicy" className="text-sm font-medium">Cancellation policy</Label>
                        <p className="m-0 text-xs text-gray-500">When to cancel or move a booking, and what a late one costs.</p>
                        <Textarea
                            id="cancellationPolicy"
                            value={merchant.cancellationPolicy}
                            onChange={(e) => patch({ cancellationPolicy: e.target.value })}
                            className="min-h-[96px] resize-y"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="depositPolicy" className="text-sm font-medium">Deposit policy</Label>
                        <p className="m-0 text-xs text-gray-500">Which bookings need a deposit, how much, and what happens to it.</p>
                        <Textarea
                            id="depositPolicy"
                            value={merchant.depositPolicy}
                            onChange={(e) => patch({ depositPolicy: e.target.value })}
                            className="min-h-[96px] resize-y"
                        />
                    </div>
                </CardBody>
            </Card>

            <Card>
                <CardHeader
                    title="FAQ notes"
                    icon={FileTextIcon}
                    description="Anything customers ask that is not a service. Press a note to edit it; drag the handle to change the order."
                    aside={<span className="text-xs text-gray-500">{merchant.faqNotes.length} {merchant.faqNotes.length === 1 ? "note" : "notes"}</span>}
                />
                <CardBody className="pt-0">
                    {merchant.faqNotes.length === 0 ? (
                        <p className="m-0 rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                            No notes yet. Anything a customer asks that is not a treatment (parking, deposits, who to
                            ask for) belongs here, or the AI has to hand it to your team.
                        </p>
                    ) : (
                        <ul className="list-plain m-0 divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 p-0">
                            {/* Keyed by index, not by label: the label is editable, and a key that
                                changes on every keystroke remounts the field and drops the cursor. */}
                            {merchant.faqNotes.map((n, i) => (
                                <FaqNoteRow
                                    key={i}
                                    index={i}
                                    count={merchant.faqNotes.length}
                                    onMove={(by) => noteOrder.moveBy(i, by)}
                                    dragging={noteOrder.dragging(i)}
                                    mark={noteOrder.mark(i)}
                                    onDragStart={noteOrder.onDragStart(i)}
                                    onDragOver={noteOrder.onDragOver(i)}
                                    onDrop={noteOrder.onDrop(i)}
                                    onDragEnd={noteOrder.onDragEnd}
                                    label={n.label}
                                    body={n.body}
                                    open={openNote === i}
                                    onToggle={() => setOpenNote(openNote === i ? null : i)}
                                    onChange={(next) => {
                                        const faqNotes = [...merchant.faqNotes];
                                        faqNotes[i] = { ...n, ...next };
                                        patch({ faqNotes });
                                    }}
                                    onRemove={() => {
                                        patch({ faqNotes: merchant.faqNotes.filter((_, x) => x !== i) });
                                        setOpenNote(null);
                                    }}
                                />
                            ))}
                        </ul>
                    )}
                    <div className="mt-3">
                        <AddButton onClick={() => {
                            patch({ faqNotes: [...merchant.faqNotes, { label: "", body: "" }] });
                            setOpenNote(merchant.faqNotes.length);
                        }}>
                            Add note
                        </AddButton>
                    </div>
                </CardBody>
            </Card>
        </div>
    );
}
